import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Bed, Bath, Ruler, Lock, ShieldCheck, BadgeCheck, Home as HomeIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { AuthImage } from "@/components/FileTools";
import { useApp } from "@/context/AppContext";
import { t, formatMoney } from "@/i18n";
import { propertyTypeLabel } from "@/lib/propertyTypes";
import Money from "@/components/Money";
import Header from "@/components/Header";
import LegalFooter from "@/components/LegalFooter";
import EditPropertyDialog from "@/components/EditPropertyDialog";
import { Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function Gallery({ images, locked }) {
  const [idx, setIdx] = useState(0);
  if (locked || !images?.length) {
    return (
      <div className="aspect-[16/10] rounded-xl bg-gradient-to-br from-[#F0E7D3] to-[#F5F0E6] flex items-center justify-center border border-[#D8CFBB]">
        <div className="text-center">
          {locked ? <Lock className="w-10 h-10 mx-auto text-[#B8A98F] mb-3" /> : <HomeIcon className="w-10 h-10 mx-auto text-[#C4B99E] mb-3" />}
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8A98F]">
            {locked ? "PROTETTO NDNCA" : "Nessuna immagine"}
          </p>
        </div>
      </div>
    );
  }
  const next = () => setIdx((i) => (i + 1) % images.length);
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  return (
    <div className="relative aspect-[16/10] rounded-xl overflow-hidden bg-[#EBE3D3]/40 border border-[#D8CFBB]" data-testid="property-gallery">
      <AuthImage fileId={images[idx]} className="h-full w-full object-cover" />
      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="prev"
            data-testid="gallery-prev"
            className="absolute start-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            aria-label="next"
            data-testid="gallery-next"
            className="absolute end-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {images.map((img, i) => (
              <button
                key={`dot-${i}-${img}`}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#C5A880]" : "w-1.5 bg-white/60"}`}
              />
            ))}
          </div>
        </>
      )}
      <Badge className="absolute top-4 start-4 bg-[#C5A880] text-black text-[10px] tracking-widest">OFF-MARKET</Badge>
    </div>
  );
}

function AgentCard({ agent, lang }) {
  if (!agent) return null;
  const since = agent.since ? new Date(agent.since).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { year: "numeric", month: "short" }) : "—";
  const initial = (agent.name || "?").charAt(0).toUpperCase();
  return (
    <div className="rounded-xl border border-[#D8CFBB] bg-[#FBF8F1] p-6" data-testid="property-agent-card">
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-4">{t("prop_agent_card", lang)}</p>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-[#2E2A22] text-[#C5A880] flex items-center justify-center text-xl font-[Cormorant_Garamond,serif]">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-lg text-[#1A1610] font-[Cormorant_Garamond,serif]" data-testid="property-agent-name">{agent.name}</p>
          <p className="text-xs text-[#7A6E5A] mt-0.5">{t("prop_agent_since", lang)} {since}</p>
          {agent.approved && (
            <div className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-800 bg-emerald-100/60 border border-emerald-200 rounded-full px-2 py-0.5">
              <BadgeCheck className="w-3 h-3" /> {t("prop_agent_approved", lang)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LockedBanner({ lang, onCta }) {
  return (
    <div className="rounded-xl border-2 border-amber-400 bg-amber-50/70 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="property-locked-banner">
      <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
        <Lock className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="text-sm text-amber-900 font-medium">{t("prop_locked_cta", lang)}</p>
        <p className="text-xs text-amber-800/80 mt-1">{t("locked", lang)}</p>
      </div>
      <Button
        onClick={onCta}
        data-testid="property-locked-cta"
        className="bg-amber-600 hover:bg-amber-700 text-white"
      >
        {t("prop_ndnca_go", lang)}
      </Button>
    </div>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { lang, user, loading } = useApp();
  const [prop, setProp] = useState(null);
  const [err, setErr] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/marketplace/property/${id}`);
      setProp(data);
    } catch (e) {
      setErr(e?.response?.status === 404 ? "not_found" : "error");
      toast.error(e?.response?.data?.detail || "Errore");
    }
  }, [id]);

  useEffect(() => {
    if (!loading) {
      if (!user) {
        nav(`/login?next=/properties/${id}`);
      } else {
        load();
      }
    }
  }, [loading, user, id, load, nav]);

  if (loading || (!prop && !err)) {
    return <div className="min-h-screen bg-[#F5F0E6] flex items-center justify-center text-[#7A6E5A]">{t("loading", lang)}</div>;
  }
  if (err === "not_found") {
    return (
      <div className="min-h-screen bg-[#F5F0E6] flex flex-col items-center justify-center p-6">
        <p className="text-4xl font-[Cormorant_Garamond,serif] text-[#2E2A22] mb-3">404</p>
        <p className="text-sm text-[#7A6E5A] mb-6">Immobile non trovato.</p>
        <Link to="/dashboard" className="text-sm underline text-[#8a7658]">← Bacheca</Link>
      </div>
    );
  }
  const p = prop || {};
  const locked = !!p.locked;
  const isAdmin = user?.role === "admin";
  return (
    <div className="min-h-screen bg-[#F5F0E6] text-[#2E2A22]" data-testid="property-detail-page">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-sm text-[#7A6E5A] hover:text-[#2E2A22] transition-colors mb-6"
          data-testid="property-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> {t("prop_back", lang)}
        </Link>
        {isAdmin && (
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => setEditOpen(true)}
              data-testid="admin-property-detail-edit"
              className="bg-[#B8965A] text-white hover:bg-[#a08248] rounded-full"
            >
              <Pencil className="h-3.5 w-3.5 me-2" /> Modifica (admin)
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT — gallery + info */}
          <div className="lg:col-span-2 space-y-6">
            <Gallery images={p.image_paths} locked={locked} />

            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-2">
                {locked ? t("locked", lang) : `${p.city}, ${p.country}`}
              </p>
              {p.property_type && (
                <p className="text-xs tracking-[0.25em] uppercase text-[#B8965A] mb-1" data-testid="property-type-label">
                  {propertyTypeLabel(p.property_type, lang)}
                </p>
              )}
              <h1 className="text-4xl sm:text-5xl font-[Cormorant_Garamond,serif] text-[#1A1610] leading-tight mb-3" data-testid="property-title">
                {p.title || "—"}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                <Money value={p.price} className="text-2xl text-[#C5A880]" data-testid="property-price" />
                {p.location && (
                  <p className="text-xs text-[#7A6E5A] flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {p.location}
                  </p>
                )}
              </div>
            </div>

            {/* Specs */}
            <div className="grid grid-cols-3 gap-3">
              <SpecCard icon={<Bed />} label={t("bedrooms", lang)} value={p.bedrooms ?? "—"} />
              <SpecCard icon={<Bath />} label={t("bathrooms", lang)} value={p.bathrooms ?? "—"} />
              <SpecCard icon={<Ruler />} label={t("area", lang)} value={(p.area_sqm ?? p.area) ? `${p.area_sqm ?? p.area} m²` : "—"} />
            </div>

            {/* Description */}
            {p.description && (
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-2">{t("prop_description", lang)}</p>
                <p className="text-[#5A5040] leading-relaxed whitespace-pre-line" data-testid="property-description">{p.description}</p>
              </div>
            )}

            {/* Verify strip */}
            <div className="flex items-center gap-2 text-xs text-[#8a7658] border-t border-[#D8CFBB] pt-4">
              <ShieldCheck className="w-4 h-4" />
              {t("prop_ref", lang)}:
              <span className="font-mono text-[#2E2A22]">{(p.id || "").slice(0, 8).toUpperCase()}</span>
            </div>
          </div>

          {/* RIGHT — sticky sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-24 self-start">
            {locked && <LockedBanner lang={lang} onCta={() => nav("/dashboard")} />}
            <AgentCard agent={p.agent} lang={lang} />
          </aside>
        </div>
      </main>
      <LegalFooter />
      {isAdmin && (
        <EditPropertyDialog
          property={editOpen ? p : null}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function SpecCard({ icon, label, value }) {
  return (
    <div className="rounded-lg border border-[#D8CFBB] bg-[#FBF8F1] p-4">
      <div className="w-8 h-8 mb-2 text-[#C5A880]">{React.cloneElement(icon, { className: "w-5 h-5" })}</div>
      <p className="text-[10px] tracking-[0.2em] uppercase text-[#B8A98F]">{label}</p>
      <p className="text-lg font-[Cormorant_Garamond,serif] text-[#1A1610] mt-1">{value}</p>
    </div>
  );
}
